import { IThemeColors } from '@wyz/types';
import {
  Badge,
  Button,
  ColumnDefinition,
  Datatable,
  Flex,
  FormatDate,
  Icon,
  IconButton,
  Text,
} from '@wyz/ui';
import { DateUtils, serialize, useQueryClient } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useState } from 'react';
import { Trans, useApi, useConfig, useLocale } from '~/common/context';
import { JOBS_STATUS_LABEL_LOOKUP } from '~/common/translators';
import { IJob, IJobList } from '~/common/types';
import { ImportDetailModal } from '~/connect/components';

const JOBS_STATUS_LOOKUP: Record<IJob['status'], IThemeColors> = {
  SUCCEEDED: 'success',
  QUEUED: 'warning',
  PROCESSING: 'info',
  FAILED: 'danger',
};
type Props = { queryKey: string[]; type: IJob['type'] };
export default function ImportTable({ queryKey, type }: Props) {
  const { locale } = useLocale();
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const queryClient = useQueryClient();
  const [jobDetail, setJobDetail] = useState<IJob | undefined>();

  const columns: Array<ColumnDefinition<IJob>> = [
    {
      field: 'filename',
      sortable: false,
      width: '20%',
      headerName: <Trans i18nKey='app.customers.import.table.filename' />,
      align: 'left',
      renderCell: (parameters) => {
        return <Text weight='medium'>{parameters.row.originalFileName}</Text>;
      },
    },
    {
      field: 'requestedBy',
      sortable: false,
      width: '50%',
      headerName: <Trans i18nKey='app.customers.import.table.requested_by' />,
      align: 'left',
      renderCell: (parameters) => {
        return <Text weight='semi-bold'>{parameters.row.createdBy}</Text>;
      },
    },
    {
      field: 'requestedAt',
      sortable: false,
      headerName: <Trans i18nKey='app.customers.import.table.requested_at' />,
      renderCell: (parameters) => {
        return (
          <Text>
            <FormatDate
              date={new Date(parameters.row.createdAt)}
              format={DateUtils.DATE_FORMAT.DMY}
              locale={locale}
            />
          </Text>
        );
      },
    },
    {
      field: 'detail',
      sortable: false,
      headerName: <Trans i18nKey='app.customers.import.table.detail' />,
      renderCell: (parameters) => {
        return (
          <Flex alignItems='center' justifyContent='center'>
            <Button
              id={`details-${parameters.row.id}`}
              onClick={() => setJobDetail(parameters.row)}
              variant='link'
            >
              <Icon icon='information-line' />
            </Button>
          </Flex>
        );
      },
    },
    {
      field: 'status',
      sortable: false,
      headerName: <Trans i18nKey='app.customers.import.table.status' />,
      renderCell: (parameters) => {
        return (
          <div>
            <Text>
              <Badge bg={JOBS_STATUS_LOOKUP[parameters.row.status]} pill>
                <Trans
                  i18nKey={JOBS_STATUS_LABEL_LOOKUP[parameters.row.status]}
                />
              </Badge>
            </Text>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <Datatable.Container<IJob>
        columns={columns}
        fetcher={(query) => {
          return callCoreApi<IJobList>(
            `/jobs/api/tenants/${tenantCode}/jobs?${serialize({
              limit: query.limit,
              page: query.page,
              sortField: 'createdAt',
              sortDirection: 'DESC',
              type: type,
            })}`,
            {},
          );
        }}
        queryKey={queryKey}
      >
        <>
          <Flex>
            <IconButton
              icon='reload'
              variant='link'
              id='refresh'
              onClick={() => {
                queryClient
                  .refetchQueries({
                    queryKey,
                  })
                  .catch(() => {});
              }}
            />
            <Datatable.Header />
          </Flex>
          <Datatable.Background>
            <Datatable.Table />
            <hr />
            <Datatable.Footer />
          </Datatable.Background>
        </>
      </Datatable.Container>
      {!isNil(jobDetail) && (
        <ImportDetailModal
          job={jobDetail}
          onClose={() => setJobDetail(undefined)}
        />
      )}
    </>
  );
}
